import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, Loader2, Leaf } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';

export default function AdminLoginPage() {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/admin/login', { password });
      // The backend sets an HttpOnly admin cookie on a successful login —
      // the browser stores it automatically (we don't touch a JWT in JS).
      if (res.data?.must_change_password) {
        // Persist a non-sensitive flag so the dashboard auto-opens the change-password
        // dialog and survives a page reload (admin shouldn't be able to dismiss by refresh).
        try {
          sessionStorage.setItem('klymart_must_change_password', '1');
        } catch (storageErr) {
          // sessionStorage may be unavailable in private browsing — fail open;
          // the admin can still rotate the password via the header button.
          if (process.env.NODE_ENV === 'development') {
            console.debug('[auth] sessionStorage unavailable:', storageErr?.message);
          }
        }
        toast.success('Signed in. Please change your password.');
      } else {
        toast.success('Welcome back.');
      }
      navigate('/admin');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid password';
      setError(typeof msg === 'string' ? msg : 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-background overflow-hidden">
      <div className="hero-aurora" aria-hidden />
      <div className="hero-grid-lines opacity-40" aria-hidden />
      <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
        <span className="h-7 w-7 rounded-md border border-border bg-secondary flex items-center justify-center text-primary">
          <Leaf className="h-3.5 w-3.5" />
        </span>
        <span className="font-display text-base">Klymart™</span>
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md px-4"
      >
        <Card className="bg-card border border-border rounded-2xl p-8 glow-ring">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                Admin
              </div>
              <h1 className="font-display text-2xl leading-tight">Sign in</h1>
            </div>
          </div>
          <form onSubmit={onSubmit} className="mt-6" noValidate>
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={ADMIN.loginPasswordInput}
              placeholder="Enter admin password"
              className="mt-1.5 h-11 bg-secondary/50 border-border focus-visible:ring-ring"
            />
            {error && (
              <div data-testid={ADMIN.loginError} className="mt-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading || !password}
              data-testid={ADMIN.loginSubmit}
              className="mt-6 w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
          <div className="mt-6 text-xs text-muted-foreground">
            For internal use only. Contact the Klymart team to request access.
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
